import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  Building2,
  Store,
  FolderKanban,
  Zap,
  Search,
  RefreshCw,
  Plus,
  Edit3,
  CheckCircle2,
  AlertCircle,
  Layers,
  MapPin,
  Phone,
  Mail,
  FileText,
  Scale,
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  Hammer,
  Calendar,
  Sparkles,
  Check,
  Loader2,
  ShieldCheck,
  Package
} from 'lucide-react';
import { PTCatalogAndBOMBuilderTab } from './PTCatalogAndBOMBuilderTab';
import { OrdersAndIngestion360Tab } from './OrdersAndIngestion360Tab';
import { MRPExplosionPlannerTab } from './MRPExplosionPlannerTab';
import { OABLineItem } from '../types/oab';
import {
  CRMClient,
  CRMBranch,
  CRMProject,
  CRMContact,
  CRMOverviewResponse,
  CLIENT_TYPES,
  CLIENT_STATUSES,
  BRANCH_TYPES,
  BRANCH_STATUSES,
  WALL_SUBSTRATES,
  FLOOR_SUBSTRATES,
  PROJECT_STATES
} from '../types/crm';
import { AllocationsResponse } from '../services/inventoryService';

interface CommercialProjectsHubModalProps {
  isOpen: boolean;
  onClose: () => void;
  token?: string | null;
  allocationsData?: AllocationsResponse | null;
  onOpenStoreAllocations?: (projectName?: string) => void;
  onOpenBOMAuditForOrder?: (order: { id: string; codigo: string; proyecto: string }) => void;
  onOpenSupplyWithDraftLines?: (lines: OABLineItem[], notes: string) => void;
  onRefreshAllocations?: () => void;
}

type HubTab = 'projects' | 'clients' | 'branches' | 'contacts' | 'pt_bom' | 'orders_360' | 'mrp_explosion' | 'cascade';

export const CommercialProjectsHubModal: React.FC<CommercialProjectsHubModalProps> = ({
  isOpen,
  onClose,
  token,
  allocationsData,
  onOpenStoreAllocations,
  onOpenBOMAuditForOrder,
  onOpenSupplyWithDraftLines,
  onRefreshAllocations
}) => {
  const [activeTab, setActiveTab] = useState<HubTab>('projects');
  const [mrpTargetOrder, setMrpTargetOrder] = useState<{ id?: string; codigo?: string }>({});
  const [data, setData] = useState<CRMOverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [toastMsg, setToastMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Filtros y Paginación Fluida (729+ Proyectos y 331+ Clientes)
  const [searchTerm, setSearchTerm] = useState('');
  const [initialOrderSearch, setInitialOrderSearch] = useState('');
  const [projectStateFilter, setProjectStateFilter] = useState<string>('TODOS');
  const [selectedClientFilter, setSelectedClientFilter] = useState<string>('TODOS');
  const [expandedClientId, setExpandedClientId] = useState<string | null>(null);
  const [visibleLimit, setVisibleLimit] = useState<number>(40);

  // Modal de Edición Individual (Cliente / Sucursal / Proyecto / Contacto)
  const [editorMode, setEditorMode] = useState<{
    open: boolean;
    entityType: 'client' | 'branch' | 'project' | 'contact';
    action: 'create' | 'update';
    id?: string;
  }>({ open: false, entityType: 'client', action: 'create' });

  const [isSaving, setIsSaving] = useState(false);
  const [supervisorPIN, setSupervisorPIN] = useState('');

  // Formulario de Contacto (BD_Contactos)
  const [contactForm, setContactForm] = useState({
    nombre: '',
    cedula: '',
    telefono: '',
    email: '',
    activo: true
  });

  // Formularios individuales
  const [clientForm, setClientForm] = useState({
    nombre: '',
    razonSocial: '',
    rif: '',
    codigoValery: '',
    tipoCliente: 'Corporativo / Empresa',
    estatus: 'Activo',
    contactoPrincipal: '',
    telefono: '',
    correo: '',
    direccionFiscal: '',
    ciudadEstado: '',
    contribuyenteEspecial: false,
    diasCredito: 0,
    limiteCreditoUSD: 0
  });

  const [branchForm, setBranchForm] = useState({
    nombre: '',
    codigoSucursal: '',
    clienteId: '',
    tipoSucursal: 'Tienda Propia',
    estatus: 'Operativa',
    ciudadEstado: '',
    centroComercial: '',
    direccion: '',
    contactoTienda: '',
    telefonoSucursal: '',
    sustratoPared: 'Mampostería / Concreto',
    sustratoPiso: 'Porcelanato / Cerámica',
    distribucionDrywallPct: 0
  });

  const [projectForm, setProjectForm] = useState({
    nombre: '',
    estado: 'En producción',
    clienteId: '',
    sucursalId: '',
    fechaInicio: new Date().toISOString().slice(0, 10),
    fechaCierre: '',
    notas: ''
  });

  // Formulario Alta en Cascada Odoo 18
  const [cascadeUseNewClient, setCascadeUseNewClient] = useState(false);
  const [cascadeClientId, setCascadeClientId] = useState('');
  const [cascadeNewClient, setCascadeNewClient] = useState({
    nombre: '',
    rif: '',
    razonSocial: '',
    codigoValery: '',
    tipoCliente: 'Corporativo / Empresa',
    ciudadEstado: '',
    telefono: ''
  });

  const [cascadeUseNewBranch, setCascadeUseNewBranch] = useState(false);
  const [cascadeBranchId, setCascadeBranchId] = useState('');
  const [cascadeNewBranch, setCascadeNewBranch] = useState({
    nombre: '',
    codigoSucursal: '',
    tipoSucursal: 'Tienda Propia',
    estatus: 'Próxima Apertura',
    ciudadEstado: '',
    centroComercial: '',
    contactoTienda: '',
    sustratoPared: 'Mampostería / Concreto',
    sustratoPiso: 'Porcelanato / Cerámica',
    distribucionDrywallPct: 0
  });

  const [cascadeProject, setCascadeProject] = useState({
    nombre: '',
    estado: 'En producción',
    fechaInicio: new Date().toISOString().slice(0, 10),
    notas: ''
  });

  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMsg({ type, text });
    setTimeout(() => setToastMsg(null), 4500);
  };

  const fetchOverview = async (force = false) => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await fetch(`/api/crm/overview${force ? '?force=1' : ''}`);
      const json: CRMOverviewResponse = await res.json();
      if (!res.ok || json.error) {
        throw new Error(json.error || `Error HTTP ${res.status}`);
      }
      setData(json);
    } catch (err: any) {
      setErrorMsg(err.message || 'Error cargando panorama CRM desde Notion ERP.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchOverview(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (editorMode.open) {
          setEditorMode(prev => ({ ...prev, open: false }));
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, editorMode.open, onClose]);

  const clients = data?.clients || [];
  const branches = data?.branches || [];
  const projects = data?.projects || [];
  const contacts = data?.contacts || [];

  useEffect(() => {
    setVisibleLimit(40);
  }, [activeTab, searchTerm, projectStateFilter, selectedClientFilter]);

  // Cruzar reservas MTO activas (StoreAllocationModal) por Proyecto
  const mtoSummaryByProject = useMemo(() => {
    const map = new Map<string, { totalBultos: number; totalValorUSD: number; insumosCount: number }>();
    if (!allocationsData?.summaryByProyectoId) return map;
    for (const [projId, sum] of Object.entries(allocationsData.summaryByProyectoId)) {
      map.set(projId, {
        totalBultos: sum.totalItems || 0,
        totalValorUSD: sum.totalUSD || 0,
        insumosCount: Array.isArray(sum.items) ? sum.items.length : (sum.totalItems || 0)
      });
    }
    return map;
  }, [allocationsData]);

  // Filtros reactivos
  const filteredProjects = useMemo(() => {
    const list = projects.filter(p => {
      if (projectStateFilter !== 'TODOS' && p.estado !== projectStateFilter) return false;
      if (selectedClientFilter !== 'TODOS' && p.clienteId !== selectedClientFilter) return false;
      if (!searchTerm.trim()) return true;
      const q = searchTerm.toLowerCase();
      return (
        p.nombre.toLowerCase().includes(q) ||
        (p.clienteNombre || '').toLowerCase().includes(q) ||
        (p.sucursalNombre || '').toLowerCase().includes(q) ||
        (p.ciudadEstado || '').toLowerCase().includes(q) ||
        (p.orders || []).some(o => o.codigo.toLowerCase().includes(q)) ||
        (p.budgets || []).some(bg => bg.codigo.toLowerCase().includes(q))
      );
    });
    return list.sort((a, b) => {
      const aDirect = a.nombre.includes('VENTAS-DIRECTAS') ? 1 : 0;
      const bDirect = b.nombre.includes('VENTAS-DIRECTAS') ? 1 : 0;
      if (aDirect !== bDirect) return aDirect - bDirect;
      const aConfirmed = (a.montoConfirmadoPedidosUSD || a.montoTotalUSD || 0) > 0 ? 1 : 0;
      const bConfirmed = (b.montoConfirmadoPedidosUSD || b.montoTotalUSD || 0) > 0 ? 1 : 0;
      if (aConfirmed !== bConfirmed) return bConfirmed - aConfirmed;
      const aHasBranch = a.sucursalId ? 1 : 0;
      const bHasBranch = b.sucursalId ? 1 : 0;
      if (aHasBranch !== bHasBranch) return bHasBranch - aHasBranch;
      const aHasClient = a.clienteId ? 1 : 0;
      const bHasClient = b.clienteId ? 1 : 0;
      if (aHasClient !== bHasClient) return bHasClient - aHasClient;
      return (b.fechaInicio || '').localeCompare(a.fechaInicio || '');
    });
  }, [projects, projectStateFilter, selectedClientFilter, searchTerm]);

  const filteredClients = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const list = !q
      ? [...clients]
      : clients.filter(c =>
          c.nombre.toLowerCase().includes(q) ||
          c.razonSocial.toLowerCase().includes(q) ||
          c.rif.toLowerCase().includes(q) ||
          c.codigoValery.toLowerCase().includes(q) ||
          c.ciudadEstado.toLowerCase().includes(q)
        );
    return list.sort((a, b) => {
      const scoreA = (a.sucursalesCount || a.sucursalIds?.length || 0) * 10 + (a.proyectosCount || a.proyectoIds?.length || 0);
      const scoreB = (b.sucursalesCount || b.sucursalIds?.length || 0) * 10 + (b.proyectosCount || b.proyectoIds?.length || 0);
      if (scoreB !== scoreA) return scoreB - scoreA;
      return a.nombre.localeCompare(b.nombre);
    });
  }, [clients, searchTerm]);

  const filteredBranches = useMemo(() => {
    return branches.filter(b => {
      if (selectedClientFilter !== 'TODOS' && b.clienteId !== selectedClientFilter) return false;
      if (!searchTerm.trim()) return true;
      const q = searchTerm.toLowerCase();
      return (
        b.nombre.toLowerCase().includes(q) ||
        (b.clienteNombre || '').toLowerCase().includes(q) ||
        b.ciudadEstado.toLowerCase().includes(q) ||
        b.centroComercial.toLowerCase().includes(q) ||
        b.sustratoPared.toLowerCase().includes(q)
      );
    });
  }, [branches, selectedClientFilter, searchTerm]);

  // Abrir editores
  const openCreateClient = () => {
    setClientForm({
      nombre: '',
      razonSocial: '',
      rif: '',
      codigoValery: '',
      tipoCliente: 'Corporativo / Empresa',
      estatus: 'Activo',
      contactoPrincipal: '',
      telefono: '',
      correo: '',
      direccionFiscal: '',
      ciudadEstado: '',
      contribuyenteEspecial: false,
      diasCredito: 0,
      limiteCreditoUSD: 0
    });
    setEditorMode({ open: true, entityType: 'client', action: 'create' });
  };

  const openEditClient = (c: CRMClient) => {
    setClientForm({
      nombre: c.nombre,
      razonSocial: c.razonSocial,
      rif: c.rif,
      codigoValery: c.codigoValery,
      tipoCliente: c.tipoCliente || 'Corporativo / Empresa',
      estatus: c.estatus || 'Activo',
      contactoPrincipal: c.contactoPrincipal,
      telefono: c.telefono,
      correo: c.correo,
      direccionFiscal: c.direccionFiscal,
      ciudadEstado: c.ciudadEstado,
      contribuyenteEspecial: c.contribuyenteEspecial,
      diasCredito: c.diasCredito,
      limiteCreditoUSD: c.limiteCreditoUSD
    });
    setEditorMode({ open: true, entityType: 'client', action: 'update', id: c.id });
  };

  const openCreateBranch = (preselectedClientId = '') => {
    setBranchForm({
      nombre: '',
      codigoSucursal: '',
      clienteId: preselectedClientId,
      tipoSucursal: 'Tienda Propia',
      estatus: 'Operativa',
      ciudadEstado: '',
      centroComercial: '',
      direccion: '',
      contactoTienda: '',
      telefonoSucursal: '',
      sustratoPared: 'Mampostería / Concreto',
      sustratoPiso: 'Porcelanato / Cerámica',
      distribucionDrywallPct: 0
    });
    setEditorMode({ open: true, entityType: 'branch', action: 'create' });
  };

  const openEditBranch = (b: CRMBranch) => {
    setBranchForm({
      nombre: b.nombre,
      codigoSucursal: b.codigoSucursal,
      clienteId: b.clienteId || '',
      tipoSucursal: b.tipoSucursal || 'Tienda Propia',
      estatus: b.estatus || 'Operativa',
      ciudadEstado: b.ciudadEstado,
      centroComercial: b.centroComercial,
      direccion: b.direccion,
      contactoTienda: b.contactoTienda,
      telefonoSucursal: b.telefonoSucursal,
      sustratoPared: b.sustratoPared || 'Mampostería / Concreto',
      sustratoPiso: b.sustratoPiso || 'Porcelanato / Cerámica',
      distribucionDrywallPct: b.distribucionDrywallPct || 0
    });
    setEditorMode({ open: true, entityType: 'branch', action: 'update', id: b.id });
  };

  const openCreateProject = (preClientId = '', preBranchId = '') => {
    setProjectForm({
      nombre: '',
      estado: 'En producción',
      clienteId: preClientId,
      sucursalId: preBranchId,
      fechaInicio: new Date().toISOString().slice(0, 10),
      fechaCierre: '',
      notas: ''
    });
    setEditorMode({ open: true, entityType: 'project', action: 'create' });
  };

  const openEditProject = (p: CRMProject) => {
    setProjectForm({
      nombre: p.nombre,
      estado: p.estado || 'En producción',
      clienteId: p.clienteId || '',
      sucursalId: p.sucursalId || '',
      fechaInicio: p.fechaInicio || '',
      fechaCierre: p.fechaCierre || '',
      notas: p.notas || ''
    });
    setEditorMode({ open: true, entityType: 'project', action: 'update', id: p.id });
  };

  const openCreateContact = () => {
    setContactForm({
      nombre: '',
      cedula: '',
      telefono: '',
      email: '',
      activo: true
    });
    setEditorMode({ open: true, entityType: 'contact', action: 'create' });
  };

  const openEditContact = (ct: CRMContact) => {
    setContactForm({
      nombre: ct.nombre,
      cedula: ct.cedula,
      telefono: ct.telefono,
      email: ct.email,
      activo: ct.activo
    });
    setEditorMode({ open: true, entityType: 'contact', action: 'update', id: ct.id });
  };

  const getAuthHeaders = () => {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  const handleSaveEntity = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const payloadData =
        editorMode.entityType === 'client'
          ? clientForm
          : editorMode.entityType === 'branch'
          ? branchForm
          : editorMode.entityType === 'contact'
          ? contactForm
          : projectForm;

      const res = await fetch('/api/crm/upsert', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          entityType: editorMode.entityType,
          action: editorMode.action,
          id: editorMode.id,
          data: payloadData,
          supervisorPIN: supervisorPIN || undefined
        })
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        throw new Error(json.error || 'Error guardando registro en Notion ERP.');
      }

      setEditorMode(prev => ({ ...prev, open: false }));
      showToast('success', 'Registro sincronizado exitosamente en Notion ERP.');
      await fetchOverview(true);
    } catch (err: any) {
      showToast('error', err.message || 'Fallo al guardar.');
    } finally {
      setIsSaving(false);
    }
  };

  // Sugeridor inteligente de nombre de Proyecto para Alta en Cascada
  const suggestCascadeProjectName = () => {
    const year = new Date().getFullYear();
    const seq = String((projects.length || 0) + 1).padStart(4, '0');
    const cliName = cascadeUseNewClient
      ? cascadeNewClient.nombre.trim().toUpperCase()
      : (clients.find(c => c.id === cascadeClientId)?.nombre || 'CLIENTE').toUpperCase();
    const brName = cascadeUseNewBranch
      ? cascadeNewBranch.nombre.trim()
      : (branches.find(b => b.id === cascadeBranchId)?.nombre || 'Tienda Principal');
    setCascadeProject(prev => ({
      ...prev,
      nombre: `PRY-${year}-${seq}: ${cliName} - ${brName}`
    }));
  };

  const handleSaveCascade = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cascadeProject.nombre.trim()) {
      showToast('error', 'Debes especificar el nombre del Proyecto de Tienda.');
      return;
    }
    setIsSaving(true);
    try {
      const res = await fetch('/api/crm/upsert', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          entityType: 'cascade',
          action: 'create',
          supervisorPIN: supervisorPIN || undefined,
          data: {
            clienteId: cascadeUseNewClient ? null : cascadeClientId,
            newClient: cascadeUseNewClient ? cascadeNewClient : null,
            sucursalId: cascadeUseNewBranch ? null : cascadeBranchId,
            newBranch: cascadeUseNewBranch ? cascadeNewBranch : null,
            project: cascadeProject
          }
        })
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        throw new Error(json.error || 'Error ejecutando alta en cascada.');
      }
      showToast('success', `Obra "${cascadeProject.nombre}" creada y vinculada en cascada.`);
      setCascadeProject({
        nombre: '',
        estado: 'En producción',
        fechaInicio: new Date().toISOString().slice(0, 10),
        notas: ''
      });
      setActiveTab('projects');
      await fetchOverview(true);
    } catch (err: any) {
      showToast('error', err.message || 'Fallo en el alta en cascada.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const getProjectStateBadge = (state: string) => {
    switch (state) {
      case 'En producción':
        return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
      case 'En despacho':
        return 'bg-sky-500/20 text-sky-300 border-sky-500/40';
      case 'Instalado':
      case 'Cerrado':
        return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40';
      default:
        return 'bg-purple-500/20 text-purple-300 border-purple-500/40';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-4 md:py-6 overflow-y-auto animate-fade-in no-print">
      <div className="bg-surface border border-borderSubtle rounded-2xl w-full max-w-[1480px] flex flex-col overflow-hidden shadow-2xl my-auto">
        {/* Top Header */}
        <div className="px-5 py-4 bg-surfaceHigh border-b border-borderSubtle flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-teal-500/20 text-teal-300 border border-teal-500/40 shadow-sm">
              <FolderKanban className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base sm:text-lg font-bold text-white tracking-tight">
                  Hub de Clientes, Sucursales y Proyectos de Tienda
                </h2>
                <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full bg-teal-500/20 text-teal-300 border border-teal-500/40">
                  Odoo 18 · PLAN-050 a PLAN-053-R
                </span>
              </div>
              <p className="text-xs text-slate-400">
                Gobernanza relacional BD_Clientes → BD_Sucursales (Sustratos de Obra) → BD_Proyectos → BD_Pedidos
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {data?.source && (
              <span className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-mono bg-page border border-borderSubtle text-slate-400">
                <span className={`w-2 h-2 rounded-full ${data.source === 'notion_live' ? 'bg-emerald-400' : 'bg-cyan-400'}`} />
                {data.source === 'notion_live' ? 'Notion Live' : 'Edge KV Cache'}
              </span>
            )}
            <button
              onClick={() => fetchOverview(true)}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-surface hover:bg-surfaceHighest text-slate-200 border border-borderSubtle transition disabled:opacity-50"
              title="Sincronizar en vivo contra Notion ERP"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-teal-400' : 'text-teal-400'}`} />
              <span>Sincronizar ERP</span>
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-surfaceHighest transition"
              title="Cerrar (Esc)"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Toast Feedback */}
        {toastMsg && (
          <div className={`px-5 py-2.5 text-xs font-medium flex items-center justify-between border-b ${
            toastMsg.type === 'success'
              ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30'
              : 'bg-rose-500/15 text-rose-200 border-rose-500/30'
          }`}>
            <div className="flex items-center gap-2">
              {toastMsg.type === 'success' ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertCircle className="w-4 h-4 text-rose-400" />}
              <span>{toastMsg.text}</span>
            </div>
            <button onClick={() => setToastMsg(null)} className="text-slate-400 hover:text-white">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* KPI Summary Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 p-4 bg-page border-b border-borderSubtle">
          <div className="bg-surface border border-borderSubtle rounded-xl p-3 flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Obras / Proyectos</p>
              <p className="text-xl font-black text-white mt-0.5">
                {data?.kpis.totalProjects ?? '—'}
                <span className="text-xs font-normal text-teal-400 ml-2">
                  ({data?.kpis.activeProjects ?? 0} activos)
                </span>
              </p>
            </div>
            <FolderKanban className="w-7 h-7 text-teal-400/70" />
          </div>

          <div className="bg-surface border border-borderSubtle rounded-xl p-3 flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Clientes (res.partner)</p>
              <p className="text-xl font-black text-white mt-0.5">
                {data?.kpis.totalClients ?? '—'}
                <span className="text-xs font-normal text-emerald-400 ml-2">
                  ({data?.kpis.activeClients ?? 0} activos)
                </span>
              </p>
            </div>
            <Building2 className="w-7 h-7 text-emerald-400/70" />
          </div>

          <div className="bg-surface border border-borderSubtle rounded-xl p-3 flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Sucursales Físicas</p>
              <p className="text-xl font-black text-white mt-0.5">
                {data?.kpis.totalBranches ?? '—'}
                <span className="text-xs font-normal text-amber-400 ml-2">
                  con ficha técnica
                </span>
              </p>
            </div>
            <Store className="w-7 h-7 text-amber-400/70" />
          </div>

          <div className="bg-surface border border-borderSubtle rounded-xl p-3 flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Pedidos 360° / Rampa</p>
              <p className="text-xl font-black text-white mt-0.5">
                638
                <span className="text-xs font-normal text-indigo-400 ml-2">
                  ({data?.kpis.totalOrders ?? 12} en Rampa)
                </span>
              </p>
            </div>
            <FileText className="w-7 h-7 text-indigo-400/70" />
          </div>
        </div>

        {/* Navigation Tabs + Quick Actions */}
        <div className="px-4 py-2.5 bg-surfaceHigh border-b border-borderSubtle flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              onClick={() => setActiveTab('projects')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold transition ${
                activeTab === 'projects'
                  ? 'bg-teal-500 text-slate-950 shadow-sm'
                  : 'text-slate-300 hover:bg-surfaceHighest'
              }`}
            >
              <FolderKanban className="w-3.5 h-3.5" />
              <span>Proyectos de Tienda ({projects.length})</span>
            </button>

            <button
              onClick={() => setActiveTab('clients')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold transition ${
                activeTab === 'clients'
                  ? 'bg-teal-500 text-slate-950 shadow-sm'
                  : 'text-slate-300 hover:bg-surfaceHighest'
              }`}
            >
              <Building2 className="w-3.5 h-3.5" />
              <span>Clientes Corporativos ({clients.length})</span>
            </button>

            <button
              onClick={() => setActiveTab('branches')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold transition ${
                activeTab === 'branches'
                  ? 'bg-teal-500 text-slate-950 shadow-sm'
                  : 'text-slate-300 hover:bg-surfaceHighest'
              }`}
            >
              <Store className="w-3.5 h-3.5" />
              <span>Sucursales y Sustratos ({branches.length})</span>
            </button>

            <button
              onClick={() => setActiveTab('contacts')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold transition ${
                activeTab === 'contacts'
                  ? 'bg-teal-500 text-slate-950 shadow-sm'
                  : 'text-slate-300 hover:bg-surfaceHighest'
              }`}
            >
              <Phone className="w-3.5 h-3.5" />
              <span>Contactos ({contacts.length})</span>
            </button>

            <button
              onClick={() => setActiveTab('pt_bom')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold transition ${
                activeTab === 'pt_bom'
                  ? 'bg-teal-500 text-slate-950 shadow-sm'
                  : 'bg-teal-500/15 text-teal-300 border border-teal-500/30 hover:bg-teal-500/25'
              }`}
            >
              <Package className="w-3.5 h-3.5" />
              <span>Catálogo PT y Recetas BOM (1282)</span>
            </button>

            <button
              onClick={() => setActiveTab('orders_360')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold transition ${
                activeTab === 'orders_360'
                  ? 'bg-sky-500 text-slate-950 shadow-sm'
                  : 'bg-sky-500/15 text-sky-300 border border-sky-500/30 hover:bg-sky-500/25'
              }`}
            >
              <FileText className="w-3.5 h-3.5" />
              <span>Pedidos e Ingesta 360° (638 · {data?.kpis?.totalOrders ?? 12} Rampa)</span>
            </button>

            <button
              onClick={() => setActiveTab('mrp_explosion')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold transition ${
                activeTab === 'mrp_explosion'
                  ? 'bg-emerald-500 text-slate-950 shadow-sm'
                  : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25'
              }`}
            >
              <Layers className="w-3.5 h-3.5" />
              <span>🧮 Explosión MRP (1 Clic)</span>
            </button>

            <button
              onClick={() => setActiveTab('cascade')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold transition ${
                activeTab === 'cascade'
                  ? 'bg-amber-400 text-slate-950 shadow-sm'
                  : 'bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25'
              }`}
            >
              <Zap className="w-3.5 h-3.5" />
              <span>Alta Rápida en Cascada (Odoo 18)</span>
            </button>
          </div>

          <div className="flex items-center gap-2">
            {activeTab === 'projects' && (
              <button
                onClick={() => openCreateProject()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 border border-teal-500/40 transition"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Nuevo Proyecto</span>
              </button>
            )}
            {activeTab === 'clients' && (
              <button
                onClick={openCreateClient}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 transition"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Nuevo Cliente</span>
              </button>
            )}
            {activeTab === 'branches' && (
              <button
                onClick={() => openCreateBranch()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 transition"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Nueva Sucursal</span>
              </button>
            )}
            {activeTab === 'contacts' && (
              <button
                onClick={openCreateContact}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 border border-sky-500/40 transition"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Nuevo Contacto</span>
              </button>
            )}
          </div>
        </div>

        {/* Filter Bar (for Projects, Clients, Branches, Contacts) */}
        {activeTab !== 'cascade' && activeTab !== 'pt_bom' && activeTab !== 'orders_360' && activeTab !== 'mrp_explosion' && (
          <div className="px-4 py-3 bg-page/60 border-b border-borderSubtle flex flex-wrap items-center justify-between gap-3">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder={
                  activeTab === 'projects'
                    ? 'Buscar por proyecto, cliente, sucursal o código de pedido...'
                    : activeTab === 'clients'
                    ? 'Buscar por nombre comercial, RIF, razón social o código Valery...'
                    : 'Buscar por sucursal, centro comercial, ciudad o sustrato...'
                }
                className="w-full pl-9 pr-3 py-1.5 text-xs bg-surface border border-borderSubtle rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-teal-400"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {(activeTab === 'projects' || activeTab === 'branches') && (
                <select
                  value={selectedClientFilter}
                  onChange={e => setSelectedClientFilter(e.target.value)}
                  className="px-2.5 py-1.5 text-xs bg-surface border border-borderSubtle rounded-lg text-slate-200 focus:outline-none focus:border-teal-400"
                >
                  <option value="TODOS">🏢 Todos los Clientes</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.nombre} {c.rif ? `(${c.rif})` : ''}
                    </option>
                  ))}
                </select>
              )}

              {activeTab === 'projects' && (
                <div className="flex items-center gap-1 overflow-x-auto">
                  {(['TODOS', ...PROJECT_STATES] as const).map(st => (
                    <button
                      key={st}
                      onClick={() => setProjectStateFilter(st)}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition ${
                        projectStateFilter === st
                          ? 'bg-teal-500 text-slate-950'
                          : 'bg-surface text-slate-400 hover:text-slate-200 border border-borderSubtle'
                      }`}
                    >
                      {st}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Main Content Body */}
        <div className="p-4 max-h-[64vh] overflow-y-auto space-y-3">
          {loading && !data ? (
            <div className="py-16 flex flex-col items-center justify-center gap-3 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
              <p className="text-xs font-medium">Hidratando grafo de Clientes, Sucursales y Proyectos desde Notion ERP...</p>
            </div>
          ) : errorMsg ? (
            <div className="p-6 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-rose-400 shrink-0" />
                <div>
                  <h4 className="text-sm font-bold">Error de conexión con Notion ERP</h4>
                  <p className="text-xs text-rose-300/80 mt-0.5">{errorMsg}</p>
                </div>
              </div>
              <button
                onClick={() => fetchOverview(true)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500/20 hover:bg-rose-500/30 text-rose-200 border border-rose-500/40"
              >
                Reintentar
              </button>
            </div>
          ) : (
            <>
              {/* ===============================================================
                  VISTA 1: PROYECTOS DE TIENDA (OBRAS)
                 =============================================================== */}
              {activeTab === 'projects' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {filteredProjects.length === 0 ? (
                    <div className="col-span-full py-12 text-center text-slate-400 text-xs">
                      No se encontraron Proyectos de Tienda con los filtros actuales.
                    </div>
                  ) : (
                    <>
                    {filteredProjects.slice(0, visibleLimit).map(proj => {
                      const mto = mtoSummaryByProject.get(proj.id);
                      return (
                        <div
                          key={proj.id}
                          className="bg-page border border-borderSubtle hover:border-teal-500/40 rounded-xl p-4 flex flex-col justify-between gap-3 transition"
                        >
                          <div className="space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${getProjectStateBadge(proj.estado)}`}>
                                    {proj.estado}
                                  </span>
                                  {proj.fechaInicio && (
                                    <span className="text-[11px] text-slate-400 flex items-center gap-1 font-mono">
                                      <Calendar className="w-3 h-3" />
                                      {proj.fechaInicio}
                                    </span>
                                  )}
                                </div>
                                <h3 className="text-sm font-bold text-white mt-1.5 leading-snug">
                                  {proj.nombre}
                                </h3>
                              </div>

                              <button
                                onClick={() => openEditProject(proj)}
                                className="p-1.5 rounded-lg bg-surface hover:bg-surfaceHighest text-slate-300 border border-borderSubtle transition shrink-0"
                                title="Editar vínculos y estado de la Obra"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                            </div>

                            {/*Jerarquía Cliente y Sucursal */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                              <div className="px-2.5 py-1.5 rounded-lg bg-surface border border-borderSubtle flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-emerald-400 shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-[10px] uppercase text-slate-400">Cliente Corporativo</p>
                                  <p className="text-xs font-semibold text-slate-200 truncate">
                                    {proj.clienteNombre} {proj.clienteRif ? `· ${proj.clienteRif}` : ''}
                                  </p>
                                </div>
                              </div>

                              <div className="px-2.5 py-1.5 rounded-lg bg-surface border border-borderSubtle flex items-center gap-2">
                                <Store className="w-4 h-4 text-amber-400 shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-[10px] uppercase text-slate-400">Sucursal / Punto Entrega</p>
                                  <p className="text-xs font-semibold text-slate-200 truncate">
                                    {proj.sucursalNombre}
                                    {proj.ciudadEstado ? ` (${proj.ciudadEstado})` : ''}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* PLAN-058 (P58-D1): Cinta Financiera Bimodal — Total Confirmado (BD_Pedidos) vs Presupuestos (BD_Presupuestos) */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-0.5">
                              <div className="px-2.5 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-between">
                                <div>
                                  <p className="text-[10px] uppercase tracking-wider text-emerald-300/90 font-semibold">
                                    Total Confirmado (Pedidos)
                                  </p>
                                  <p className="text-xs font-black text-emerald-200 font-mono">
                                    ${(proj.montoConfirmadoPedidosUSD ?? proj.montoTotalUSD ?? 0).toLocaleString('en-US', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                    })}{' '}
                                    USD
                                  </p>
                                </div>
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold">
                                  {proj.orders?.length || 0} PED
                                </span>
                              </div>

                              <div className="px-2.5 py-1.5 rounded-lg bg-sky-500/10 border border-sky-500/30 flex items-center justify-between">
                                <div>
                                  <p className="text-[10px] uppercase tracking-wider text-sky-300/90 font-semibold">
                                    Cotizado (BD_Presupuestos)
                                  </p>
                                  <p className="text-xs font-black text-sky-200 font-mono">
                                    ${(proj.montoCotizadoPresupuestosUSD ?? 0).toLocaleString('en-US', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                    })}{' '}
                                    USD
                                  </p>
                                </div>
                                <span
                                  className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-300 font-bold"
                                  title="Monto cotizado en BD_Presupuestos separado del Total Confirmado (sin duplicar)"
                                >
                                  {proj.budgets?.length || 0} PRS
                                </span>
                              </div>
                            </div>

                            {/* Ingeniería de Instalación (Sustratos heredados de la sucursal) */}
                            {proj.sustratoPared && (
                              <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px] border border-slate-700">
                                  <Hammer className="w-3 h-3 text-amber-400" />
                                  Pared: <strong>{proj.sustratoPared}</strong>
                                  {proj.distribucionDrywallPct ? ` (${proj.distribucionDrywallPct}% Drywall)` : ''}
                                </span>
                                {proj.sustratoPiso && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px] border border-slate-700">
                                    Piso: <strong>{proj.sustratoPiso}</strong>
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Pedidos Vinculados */}
                            <div className="pt-1">
                              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
                                Pedidos Vinculados ({proj.orders?.length || 0})
                              </p>
                              {proj.orders && proj.orders.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5">
                                  {proj.orders.slice(0, 6).map(ord => (
                                    <div
                                      key={ord.id}
                                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-indigo-500/10 border border-indigo-500/30 text-indigo-200 text-xs"
                                    >
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setInitialOrderSearch(ord.codigo);
                                          setActiveTab('orders_360');
                                        }}
                                        className="font-mono font-bold hover:text-sky-300 hover:underline transition"
                                        title={`Inspeccionar pedido ${ord.codigo} y sus renglones 4-UP en Pedidos e Ingesta 360°`}
                                      >
                                        {ord.codigo}
                                      </button>
                                      {ord.montoUSD > 0 && (
                                        <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                          ${ord.montoUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                      )}
                                      <span className="text-[10px] px-1 rounded bg-indigo-500/20 text-indigo-300">
                                        {ord.estado}
                                      </span>
                                      {onOpenBOMAuditForOrder && (
                                        <button
                                          onClick={() => {
                                            onClose();
                                            onOpenBOMAuditForOrder({
                                              id: ord.id,
                                              codigo: ord.codigo,
                                              proyecto: proj.nombre
                                            });
                                          }}
                                          className="ml-1 text-[10px] font-semibold text-teal-300 hover:text-white underline flex items-center gap-0.5"
                                          title="Abrir Balance y Auditoría de Cierre BOM para este pedido"
                                        >
                                          <Scale className="w-3 h-3" />
                                          BOM
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                  {proj.orders.length > 6 && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-surface text-slate-400 text-[11px] font-mono border border-borderSubtle">
                                      +{proj.orders.length - 6} más
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <p className="text-[11px] text-slate-500 italic">
                                  Sin órdenes vinculadas aún en BD_Pedidos.
                                </p>
                              )}

                              {/* Presupuestos Vinculados (BD_Presupuestos — P58-D1) */}
                              {proj.budgets && proj.budgets.length > 0 && (
                                <div className="mt-2 pt-1.5 border-t border-borderSubtle/60">
                                  <p className="text-[10px] uppercase tracking-wider text-sky-400/90 font-semibold mb-1">
                                    Presupuestos Vinculados ({proj.budgets.length})
                                  </p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {proj.budgets.slice(0, 4).map(bg => (
                                      <span
                                        key={bg.id}
                                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-sky-500/10 border border-sky-500/30 text-sky-200 text-[11px] font-mono"
                                      >
                                        <FileText className="w-3 h-3 text-sky-400" />
                                        <strong>{bg.codigo}</strong>
                                        {bg.totalUSD > 0 && (
                                          <span className="text-sky-300">
                                            (${bg.totalUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                                          </span>
                                        )}
                                        <span className="text-[9px] px-1 rounded bg-sky-500/20 text-sky-300">
                                          {bg.estadoComercial}
                                        </span>
                                      </span>
                                    ))}
                                    {proj.budgets.length > 4 && (
                                      <span className="text-[10px] text-slate-400 font-mono">
                                        +{proj.budgets.length - 4} más
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Footer: Integración en Vivo con Asignación Tiendas (MTO) */}
                          <div className="pt-2.5 border-t border-borderSubtle flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              {mto && mto.totalBultos > 0 ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-300 text-xs font-semibold">
                                  <Layers className="w-3.5 h-3.5 text-amber-400" />
                                  {mto.insumosCount} insumos reservados (${mto.totalValorUSD.toFixed(2)} USD)
                                </span>
                              ) : (
                                <span className="text-[11px] text-slate-500">
                                  Sin reservas MTO activas en almacén
                                </span>
                              )}
                            </div>

                            {onOpenStoreAllocations && (
                              <button
                                onClick={() => {
                                  onClose();
                                  onOpenStoreAllocations(proj.nombre);
                                }}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-surface hover:bg-surfaceHighest text-amber-300 border border-amber-500/30 transition"
                              >
                                <span>Ir a Asignación Tiendas</span>
                                <ArrowUpRight className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {filteredProjects.length > visibleLimit && (
                      <div className="col-span-full pt-2 flex justify-center">
                        <button
                          onClick={() => setVisibleLimit(prev => prev + 50)}
                          className="px-4 py-2 rounded-xl text-xs font-bold bg-surfaceHigh hover:bg-surfaceHighest text-teal-300 border border-teal-500/30 transition"
                        >
                          Mostrar más proyectos (+50) — Mostrando {visibleLimit} de {filteredProjects.length}
                        </button>
                      </div>
                    )}
                    </>
                  )}
                </div>
              )}

              {/* ===============================================================
                  VISTA 2: CLIENTES CORPORATIVOS (BD_Clientes)
                 =============================================================== */}
              {activeTab === 'clients' && (
                <div className="space-y-2.5">
                  {filteredClients.length === 0 ? (
                    <div className="py-12 text-center text-slate-400 text-xs">
                      No se encontraron Clientes con el criterio buscado.
                    </div>
                  ) : (
                    <>
                    {filteredClients.slice(0, visibleLimit).map(cli => {
                      const isExpanded = expandedClientId === cli.id;
                      const cliBranches = branches.filter(b => b.clienteId === cli.id || cli.sucursalIds.includes(b.id));
                      const cliProjects = projects.filter(p => p.clienteId === cli.id || cli.proyectoIds.includes(p.id));

                      return (
                        <div
                          key={cli.id}
                          className="bg-page border border-borderSubtle rounded-xl overflow-hidden transition"
                        >
                          <div className="p-4 flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-start gap-3 min-w-[240px] flex-1">
                              <div className="p-2 rounded-lg bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 mt-0.5">
                                <Building2 className="w-5 h-5" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="text-sm font-bold text-white">{cli.nombre}</h3>
                                  {cli.rif && (
                                    <span className="px-2 py-0.5 rounded bg-surface text-slate-300 font-mono text-[11px] border border-borderSubtle">
                                      {cli.rif}
                                    </span>
                                  )}
                                  {cli.codigoValery && (
                                    <span className="px-2 py-0.5 rounded bg-indigo-500/15 text-indigo-300 font-mono text-[10px] border border-indigo-500/30">
                                      Valery: {cli.codigoValery}
                                    </span>
                                  )}
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                    cli.estatus === 'Activo'
                                      ? 'bg-emerald-500/20 text-emerald-300'
                                      : 'bg-slate-700 text-slate-300'
                                  }`}>
                                    {cli.estatus}
                                  </span>
                                </div>

                                <p className="text-xs text-slate-400 mt-1">
                                  {cli.razonSocial || cli.tipoCliente}
                                  {cli.ciudadEstado ? ` · 📍 ${cli.ciudadEstado}` : ''}
                                  {cli.contactoPrincipal ? ` · 👤 ${cli.contactoPrincipal}` : ''}
                                  {cli.telefono ? ` · 📞 ${cli.telefono}` : ''}
                                </p>
                              </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                              <span className="px-2.5 py-1 rounded-lg bg-surface border border-borderSubtle text-xs text-slate-300">
                                🏪 <strong>{cliBranches.length}</strong> Sucursales
                              </span>
                              <span className="px-2.5 py-1 rounded-lg bg-surface border border-borderSubtle text-xs text-slate-300">
                                🏗️ <strong>{cliProjects.length}</strong> Obras
                              </span>

                              <button
                                onClick={() => openCreateBranch(cli.id)}
                                className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/30 transition"
                              >
                                + Sucursal
                              </button>
                              <button
                                onClick={() => openCreateProject(cli.id, cliBranches[0]?.id || '')}
                                className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-teal-500/15 hover:bg-teal-500/25 text-teal-300 border border-teal-500/30 transition"
                              >
                                + Proyecto
                              </button>
                              <button
                                onClick={() => openEditClient(cli)}
                                className="p-1.5 rounded-lg bg-surface hover:bg-surfaceHighest text-slate-300 border border-borderSubtle transition"
                                title="Editar Ficha de Cliente"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => setExpandedClientId(isExpanded ? null : cli.id)}
                                className="p-1.5 rounded-lg bg-surface hover:bg-surfaceHighest text-slate-300 border border-borderSubtle transition"
                                title="Ver árbol de Sucursales y Obras de este Cliente"
                              >
                                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="px-4 py-3 bg-surface/70 border-t border-borderSubtle grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <h4 className="text-[11px] font-bold uppercase tracking-wider text-amber-400 mb-2">
                                  🏪 Sucursales Físicas del Cliente ({cliBranches.length})
                                </h4>
                                {cliBranches.length === 0 ? (
                                  <p className="text-xs text-slate-500 italic">Sin sucursales registradas para este cliente.</p>
                                ) : (
                                  <div className="space-y-1.5">
                                    {cliBranches.map(b => (
                                      <div key={b.id} className="p-2 rounded-lg bg-page border border-borderSubtle flex items-center justify-between text-xs">
                                        <div>
                                          <p className="font-semibold text-slate-200">{b.nombre}</p>
                                          <p className="text-[11px] text-slate-400">
                                            {b.ciudadEstado || 'Sin ciudad'} · Pared: {b.sustratoPared}
                                          </p>
                                        </div>
                                        <button
                                          onClick={() => openEditBranch(b)}
                                          className="text-slate-400 hover:text-white p-1"
                                        >
                                          <Edit3 className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              <div>
                                <h4 className="text-[11px] font-bold uppercase tracking-wider text-teal-400 mb-2">
                                  🏗️ Proyectos / Obras del Cliente ({cliProjects.length})
                                </h4>
                                {cliProjects.length === 0 ? (
                                  <p className="text-xs text-slate-500 italic">Sin obras registradas para este cliente.</p>
                                ) : (
                                  <div className="space-y-1.5">
                                    {cliProjects.map(p => (
                                      <div key={p.id} className="p-2 rounded-lg bg-page border border-borderSubtle flex items-center justify-between text-xs">
                                        <div>
                                          <p className="font-semibold text-slate-200">{p.nombre}</p>
                                          <p className="text-[11px] text-slate-400">
                                            Estado: {p.estado} · {p.ordersCount || 0} pedidos
                                          </p>
                                        </div>
                                        <button
                                          onClick={() => openEditProject(p)}
                                          className="text-slate-400 hover:text-white p-1"
                                        >
                                          <Edit3 className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {filteredClients.length > visibleLimit && (
                      <div className="pt-2 flex justify-center">
                        <button
                          onClick={() => setVisibleLimit(prev => prev + 50)}
                          className="px-4 py-2 rounded-xl text-xs font-bold bg-surfaceHigh hover:bg-surfaceHighest text-emerald-300 border border-emerald-500/30 transition"
                        >
                          Mostrar más clientes (+50) — Mostrando {visibleLimit} de {filteredClients.length}
                        </button>
                      </div>
                    )}
                    </>
                  )}
                </div>
              )}

              {/* ===============================================================
                  VISTA 3: SUCURSALES FÍSICAS Y FICHA TÉCNICA DE SUSTRATOS
                 =============================================================== */}
              {activeTab === 'branches' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {filteredBranches.length === 0 ? (
                    <div className="col-span-full py-12 text-center text-slate-400 text-xs">
                      No se encontraron Sucursales Físicas con los filtros seleccionados.
                    </div>
                  ) : (
                    filteredBranches.map(b => (
                      <div
                        key={b.id}
                        className="bg-page border border-borderSubtle hover:border-amber-500/40 rounded-xl p-4 flex flex-col justify-between gap-3 transition"
                      >
                        <div className="space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                                {b.estatus} · {b.tipoSucursal}
                              </span>
                              <h3 className="text-sm font-bold text-white mt-1.5">{b.nombre}</h3>
                              <p className="text-xs text-emerald-400 font-medium">
                                🏢 {b.clienteNombre}
                              </p>
                            </div>
                            <button
                              onClick={() => openEditBranch(b)}
                              className="p-1.5 rounded-lg bg-surface hover:bg-surfaceHighest text-slate-300 border border-borderSubtle"
                              title="Editar Sucursal y Sustratos"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          <div className="text-xs text-slate-400 space-y-1 pt-1">
                            {(b.ciudadEstado || b.centroComercial) && (
                              <p className="flex items-center gap-1.5">
                                <MapPin className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                <span>
                                  {[b.centroComercial, b.ciudadEstado].filter(Boolean).join(' · ')}
                                </span>
                              </p>
                            )}
                            {b.contactoTienda && (
                              <p className="flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                <span>
                                  Rampa/Tienda: {b.contactoTienda} {b.telefonoSucursal ? `(${b.telefonoSucursal})` : ''}
                                </span>
                              </p>
                            )}
                          </div>

                          {/* Bloque de Ingeniería de Instalación en Obra */}
                          <div className="p-2.5 rounded-lg bg-surface border border-borderSubtle space-y-1">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-400 flex items-center gap-1">
                              <Hammer className="w-3 h-3" />
                              Ingeniería de Fijación en Obra
                            </p>
                            <div className="grid grid-cols-2 gap-1 text-[11px] text-slate-300">
                              <div>
                                <span className="text-slate-500">Pared:</span>{' '}
                                <strong>{b.sustratoPared || 'Por definir'}</strong>
                              </div>
                              <div>
                                <span className="text-slate-500">Piso:</span>{' '}
                                <strong>{b.sustratoPiso || 'Por definir'}</strong>
                              </div>
                            </div>
                            {b.distribucionDrywallPct > 0 && (
                              <p className="text-[10px] text-amber-300">
                                Distribución Mixta: {b.distribucionDrywallPct}% Drywall
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="pt-2 border-t border-borderSubtle flex items-center justify-between">
                          <span className="text-[11px] text-slate-400">
                            🏗️ {b.proyectosCount || 0} proyectos asociados
                          </span>
                          <button
                            onClick={() => openCreateProject(b.clienteId || '', b.id)}
                            className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-teal-500/15 hover:bg-teal-500/25 text-teal-300 border border-teal-500/30 transition"
                          >
                            + Abrir Proyecto Aquí
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* ===============================================================
                  VISTA 3.5: CONTACTOS DE OBRA / INTERLOCUTORES (BD_Contactos)
                 =============================================================== */}
              {activeTab === 'contacts' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {contacts.length === 0 ? (
                    <div className="col-span-full py-12 text-center text-slate-400 text-xs">
                      No hay Contactos registrados aún en BD_Contactos.
                    </div>
                  ) : (
                    contacts
                      .filter(ct => !searchTerm.trim() || ct.nombre.toLowerCase().includes(searchTerm.toLowerCase()) || ct.cedula.toLowerCase().includes(searchTerm.toLowerCase()))
                      .map(ct => (
                        <div
                          key={ct.id}
                          className="bg-page border border-borderSubtle hover:border-sky-500/40 rounded-xl p-4 flex flex-col justify-between gap-3 transition"
                        >
                          <div className="space-y-1.5">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                  ct.activo ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-700 text-slate-300'
                                }`}>
                                  {ct.activo ? 'Activo' : 'Inactivo'}
                                </span>
                                <h3 className="text-sm font-bold text-white mt-1">{ct.nombre}</h3>
                              </div>
                              <button
                                onClick={() => openEditContact(ct)}
                                className="p-1.5 rounded-lg bg-surface hover:bg-surfaceHighest text-slate-300 border border-borderSubtle"
                                title="Editar Contacto"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            {ct.cedula && <p className="text-xs text-slate-400 font-mono">C.I./Doc: {ct.cedula}</p>}
                            {ct.telefono && (
                              <p className="text-xs text-slate-300 flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5 text-sky-400" />
                                {ct.telefono}
                              </p>
                            )}
                            {ct.email && (
                              <p className="text-xs text-slate-300 flex items-center gap-1.5">
                                <Mail className="w-3.5 h-3.5 text-sky-400" />
                                {ct.email}
                              </p>
                            )}
                          </div>
                        </div>
                      ))
                  )}
                </div>
              )}

              {/* ===============================================================
                  VISTA 4: ALTA RÁPIDA EN CASCADA (ODOO 18)
                 =============================================================== */}
              {activeTab === 'cascade' && (
                <form onSubmit={handleSaveCascade} className="bg-page border border-borderSubtle rounded-xl p-5 space-y-5">
                  <div className="flex items-center justify-between border-b border-borderSubtle pb-3">
                    <div>
                      <h3 className="text-sm font-bold text-amber-300 flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        Asistente de Alta en Cascada Odoo 18 (Cliente → Sucursal → Proyecto de Tienda)
                      </h3>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Registra una nueva Obra de Tienda seleccionando o creando al vuelo su Cliente y su Sucursal física en un solo paso transaccional.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* PASO 1: CLIENTE */}
                    <div className="p-4 rounded-xl bg-surface border border-borderSubtle space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">
                          1. Cliente Corporativo
                        </span>
                        <button
                          type="button"
                          onClick={() => setCascadeUseNewClient(!cascadeUseNewClient)}
                          className="text-[11px] font-semibold text-teal-300 hover:underline"
                        >
                          {cascadeUseNewClient ? 'Usar cliente existente' : '+ Crear nuevo cliente'}
                        </button>
                      </div>

                      {!cascadeUseNewClient ? (
                        <div>
                          <label className="block text-[11px] text-slate-400 mb-1">Seleccionar Cliente de BD_Clientes</label>
                          <select
                            value={cascadeClientId}
                            onChange={e => {
                              setCascadeClientId(e.target.value);
                              setCascadeBranchId('');
                            }}
                            className="w-full px-3 py-2 text-xs bg-page border border-borderSubtle rounded-lg text-white"
                          >
                            <option value="">-- Seleccionar Cliente --</option>
                            {clients.map(c => (
                              <option key={c.id} value={c.id}>
                                {c.nombre} {c.rif ? `(${c.rif})` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <input
                            type="text"
                            required
                            placeholder="Nombre Comercial (ej: FARMATODO) *"
                            value={cascadeNewClient.nombre}
                            onChange={e => setCascadeNewClient({ ...cascadeNewClient, nombre: e.target.value })}
                            className="w-full px-3 py-1.5 text-xs bg-page border border-borderSubtle rounded-lg text-white"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              placeholder="RIF (ej: J-00020200-1)"
                              value={cascadeNewClient.rif}
                              onChange={e => setCascadeNewClient({ ...cascadeNewClient, rif: e.target.value })}
                              className="w-full px-3 py-1.5 text-xs bg-page border border-borderSubtle rounded-lg text-white"
                            />
                            <input
                              type="text"
                              placeholder="Código Valery"
                              value={cascadeNewClient.codigoValery}
                              onChange={e => setCascadeNewClient({ ...cascadeNewClient, codigoValery: e.target.value })}
                              className="w-full px-3 py-1.5 text-xs bg-page border border-borderSubtle rounded-lg text-white"
                            />
                          </div>
                          <input
                            type="text"
                            placeholder="Razón Social Fiscal"
                            value={cascadeNewClient.razonSocial}
                            onChange={e => setCascadeNewClient({ ...cascadeNewClient, razonSocial: e.target.value })}
                            className="w-full px-3 py-1.5 text-xs bg-page border border-borderSubtle rounded-lg text-white"
                          />
                        </div>
                      )}
                    </div>

                    {/* PASO 2: SUCURSAL */}
                    <div className="p-4 rounded-xl bg-surface border border-borderSubtle space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-amber-400 uppercase tracking-wider">
                          2. Sucursal Física (Tienda)
                        </span>
                        <button
                          type="button"
                          onClick={() => setCascadeUseNewBranch(!cascadeUseNewBranch)}
                          className="text-[11px] font-semibold text-teal-300 hover:underline"
                        >
                          {cascadeUseNewBranch ? 'Usar sucursal existente' : '+ Crear nueva sucursal'}
                        </button>
                      </div>

                      {!cascadeUseNewBranch ? (
                        <div>
                          <label className="block text-[11px] text-slate-400 mb-1">Seleccionar Sucursal de BD_Sucursales</label>
                          <select
                            value={cascadeBranchId}
                            onChange={e => setCascadeBranchId(e.target.value)}
                            className="w-full px-3 py-2 text-xs bg-page border border-borderSubtle rounded-lg text-white"
                          >
                            <option value="">-- Seleccionar Sucursal --</option>
                            {branches
                              .filter(b => !cascadeClientId || b.clienteId === cascadeClientId)
                              .map(b => (
                                <option key={b.id} value={b.id}>
                                  {b.nombre} ({b.ciudadEstado || b.sustratoPared})
                                </option>
                              ))}
                          </select>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <input
                            type="text"
                            required
                            placeholder="Nombre Sucursal (ej: Sambil Valencia) *"
                            value={cascadeNewBranch.nombre}
                            onChange={e => setCascadeNewBranch({ ...cascadeNewBranch, nombre: e.target.value })}
                            className="w-full px-3 py-1.5 text-xs bg-page border border-borderSubtle rounded-lg text-white"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              placeholder="Ciudad / Estado"
                              value={cascadeNewBranch.ciudadEstado}
                              onChange={e => setCascadeNewBranch({ ...cascadeNewBranch, ciudadEstado: e.target.value })}
                              className="w-full px-3 py-1.5 text-xs bg-page border border-borderSubtle rounded-lg text-white"
                            />
                            <input
                              type="text"
                              placeholder="C.C. / Mall"
                              value={cascadeNewBranch.centroComercial}
                              onChange={e => setCascadeNewBranch({ ...cascadeNewBranch, centroComercial: e.target.value })}
                              className="w-full px-3 py-1.5 text-xs bg-page border border-borderSubtle rounded-lg text-white"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <select
                              value={cascadeNewBranch.sustratoPared}
                              onChange={e => setCascadeNewBranch({ ...cascadeNewBranch, sustratoPared: e.target.value })}
                              className="w-full px-2 py-1.5 text-xs bg-page border border-borderSubtle rounded-lg text-white"
                            >
                              {WALL_SUBSTRATES.map(w => (
                                <option key={w} value={w}>{w}</option>
                              ))}
                            </select>
                            <select
                              value={cascadeNewBranch.sustratoPiso}
                              onChange={e => setCascadeNewBranch({ ...cascadeNewBranch, sustratoPiso: e.target.value })}
                              className="w-full px-2 py-1.5 text-xs bg-page border border-borderSubtle rounded-lg text-white"
                            >
                              {FLOOR_SUBSTRATES.map(f => (
                                <option key={f} value={f}>{f}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* PASO 3: PROYECTO DE TIENDA */}
                    <div className="p-4 rounded-xl bg-surface border border-borderSubtle space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-teal-400 uppercase tracking-wider">
                          3. Proyecto de Tienda (Obra)
                        </span>
                        <button
                          type="button"
                          onClick={suggestCascadeProjectName}
                          className="text-[11px] font-semibold text-amber-300 hover:underline flex items-center gap-1"
                        >
                          <Sparkles className="w-3 h-3" />
                          Autocompletar Código
                        </button>
                      </div>

                      <div className="space-y-2">
                        <input
                          type="text"
                          required
                          placeholder="PRY-2026-0050: CLIENTE - Sucursal *"
                          value={cascadeProject.nombre}
                          onChange={e => setCascadeProject({ ...cascadeProject, nombre: e.target.value })}
                          className="w-full px-3 py-1.5 text-xs bg-page border border-borderSubtle rounded-lg text-white font-semibold"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <select
                            value={cascadeProject.estado}
                            onChange={e => setCascadeProject({ ...cascadeProject, estado: e.target.value })}
                            className="w-full px-2.5 py-1.5 text-xs bg-page border border-borderSubtle rounded-lg text-white"
                          >
                            {PROJECT_STATES.map(st => (
                              <option key={st} value={st}>{st}</option>
                            ))}
                          </select>
                          <input
                            type="date"
                            value={cascadeProject.fechaInicio}
                            onChange={e => setCascadeProject({ ...cascadeProject, fechaInicio: e.target.value })}
                            className="w-full px-2.5 py-1.5 text-xs bg-page border border-borderSubtle rounded-lg text-white"
                          />
                        </div>
                        <textarea
                          rows={2}
                          placeholder="Notas de alcance de la obra o requerimientos de entrega..."
                          value={cascadeProject.notas}
                          onChange={e => setCascadeProject({ ...cascadeProject, notas: e.target.value })}
                          className="w-full px-3 py-1.5 text-xs bg-page border border-borderSubtle rounded-lg text-white"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-2 border-t border-borderSubtle">
                    <button
                      type="submit"
                      disabled={isSaving}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold bg-teal-500 hover:bg-teal-400 text-slate-950 shadow-lg transition disabled:opacity-50"
                    >
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      <span>Ejecutar Alta en Cascada en Notion ERP</span>
                    </button>
                  </div>
                </form>
              )}

              {/* ===============================================================
                  VISTA 6: CATÁLOGO MAESTRO MOBILIARIO PT Y CONSTRUCTOR BOM (PLAN-051)
                 =============================================================== */}
              {activeTab === 'pt_bom' && (
                <PTCatalogAndBOMBuilderTab token={token} onNotify={showToast} />
              )}

              {/* ===============================================================
                  VISTA 7: GESTIÓN E INGESTA HÍBRIDA 360° DE PEDIDOS Y LÍNEAS 4-UP (PLAN-052)
                 =============================================================== */}
              {activeTab === 'orders_360' && (
                <OrdersAndIngestion360Tab
                  token={token}
                  projects={projects}
                  clients={clients}
                  branches={branches}
                  initialSearchTerm={initialOrderSearch}
                  onOrderSaved={() => fetchOverview(true)}
                  onOpenMRPExplosion={order => {
                    setMrpTargetOrder({ id: order.id, codigo: order.codigo });
                    setActiveTab('mrp_explosion');
                  }}
                  onOpenStoreAllocations={
                    onOpenStoreAllocations
                      ? projectName => {
                          onClose();
                          onOpenStoreAllocations(projectName);
                        }
                      : undefined
                  }
                  onOpenBOMAuditForOrder={
                    onOpenBOMAuditForOrder
                      ? order => {
                          onClose();
                          onOpenBOMAuditForOrder(order);
                        }
                      : undefined
                  }
                  onNotify={showToast}
                />
              )}

              {/* ===============================================================
                  VISTA 8: PLANIFICADOR DE EXPLOSIÓN MRP EX-ANTE EN 3 CAPAS (PLAN-053)
                 =============================================================== */}
              {activeTab === 'mrp_explosion' && (
                <MRPExplosionPlannerTab
                  token={token}
                  projects={projects}
                  initialOrderId={mrpTargetOrder.id}
                  initialOrderCodigo={mrpTargetOrder.codigo}
                  onOpenStoreAllocations={
                    onOpenStoreAllocations
                      ? projectName => {
                          onClose();
                          onOpenStoreAllocations(projectName);
                        }
                      : undefined
                  }
                  onOpenBOMAuditForOrder={
                    onOpenBOMAuditForOrder
                      ? order => {
                          onClose();
                          onOpenBOMAuditForOrder(order);
                        }
                      : undefined
                  }
                  onOpenSupplyWithDraftLines={
                    onOpenSupplyWithDraftLines
                      ? (lines, notes) => {
                          onClose();
                          onOpenSupplyWithDraftLines(lines, notes);
                        }
                      : undefined
                  }
                  onRefreshAllocations={onRefreshAllocations}
                  onNotify={showToast}
                />
              )}
            </>
          )}
        </div>

        {/* Footer Roadmap Banner */}
        <div className="px-5 py-2.5 bg-surfaceHigh border-t border-borderSubtle flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-400">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-teal-400" />
            <span>
              <strong>Arquitectura Odoo 18 100% Operativa (4/4 Fases):</strong> PLAN-050 (CRM/Sucursales/Obras) · PLAN-051 (Catálogo PT 1.338 y BOM) · PLAN-052 (Pedidos 360° 4-UP) · PLAN-053 (Explosión MRP 3 Capas + Reserva MTO y OAB a 1 Clic).
            </span>
          </div>
          <button
            onClick={onClose}
            className="px-3 py-1 rounded-lg bg-surface hover:bg-surfaceHighest text-slate-200 border border-borderSubtle font-semibold"
          >
            Cerrar
          </button>
        </div>
      </div>

      {/* =====================================================================
          SUB-MODAL DE CREACIÓN / EDICIÓN INDIVIDUAL (Client / Branch / Project)
         ===================================================================== */}
      {editorMode.open && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/75 p-4">
          <form
            onSubmit={handleSaveEntity}
            className="bg-surface border border-borderSubtle rounded-2xl w-full max-w-xl overflow-hidden shadow-2xl animate-fade-in"
          >
            <div className="px-5 py-3.5 bg-surfaceHigh border-b border-borderSubtle flex items-center justify-between">
              <h3 className="text-sm font-bold text-white">
                {editorMode.action === 'create' ? '➕ Crear' : '✏️ Editar'}{' '}
                {editorMode.entityType === 'client'
                  ? 'Cliente Corporativo (BD_Clientes)'
                  : editorMode.entityType === 'branch'
                  ? 'Sucursal Física y Sustratos (BD_Sucursales)'
                  : 'Proyecto de Tienda / Obra (BD_Proyectos)'}
              </h3>
              <button
                type="button"
                onClick={() => setEditorMode(prev => ({ ...prev, open: false }))}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto text-xs">
              {editorMode.entityType === 'client' && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-400 mb-1">Nombre Comercial *</label>
                      <input
                        type="text"
                        required
                        value={clientForm.nombre}
                        onChange={e => setClientForm({ ...clientForm, nombre: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1">RIF / Documento Fiscal</label>
                      <input
                        type="text"
                        value={clientForm.rif}
                        onChange={e => setClientForm({ ...clientForm, rif: e.target.value })}
                        placeholder="J-00000000-0"
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white font-mono"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-400 mb-1">Razón Social</label>
                      <input
                        type="text"
                        value={clientForm.razonSocial}
                        onChange={e => setClientForm({ ...clientForm, razonSocial: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1">Código Valery ERP</label>
                      <input
                        type="text"
                        value={clientForm.codigoValery}
                        onChange={e => setClientForm({ ...clientForm, codigoValery: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white font-mono"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-400 mb-1">Tipo de Cliente</label>
                      <select
                        value={clientForm.tipoCliente}
                        onChange={e => setClientForm({ ...clientForm, tipoCliente: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      >
                        {CLIENT_TYPES.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1">Estatus</label>
                      <select
                        value={clientForm.estatus}
                        onChange={e => setClientForm({ ...clientForm, estatus: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      >
                        {CLIENT_STATUSES.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-slate-400 mb-1">Contacto Principal</label>
                      <input
                        type="text"
                        value={clientForm.contactoPrincipal}
                        onChange={e => setClientForm({ ...clientForm, contactoPrincipal: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1">Teléfono</label>
                      <input
                        type="text"
                        value={clientForm.telefono}
                        onChange={e => setClientForm({ ...clientForm, telefono: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1">Ciudad / Estado</label>
                      <input
                        type="text"
                        value={clientForm.ciudadEstado}
                        onChange={e => setClientForm({ ...clientForm, ciudadEstado: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      />
                    </div>
                  </div>
                </>
              )}

              {editorMode.entityType === 'branch' && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-400 mb-1">Nombre de la Sucursal *</label>
                      <input
                        type="text"
                        required
                        value={branchForm.nombre}
                        onChange={e => setBranchForm({ ...branchForm, nombre: e.target.value })}
                        placeholder="Ej: LC Waikiki - Sambil Valencia"
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1">Cliente Propietario (BD_Clientes)</label>
                      <select
                        value={branchForm.clienteId}
                        onChange={e => setBranchForm({ ...branchForm, clienteId: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      >
                        <option value="">-- Sin vincular --</option>
                        {clients.map(c => (
                          <option key={c.id} value={c.id}>{c.nombre}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-slate-400 mb-1">Tipo de Sucursal</label>
                      <select
                        value={branchForm.tipoSucursal}
                        onChange={e => setBranchForm({ ...branchForm, tipoSucursal: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      >
                        {BRANCH_TYPES.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1">Estatus</label>
                      <select
                        value={branchForm.estatus}
                        onChange={e => setBranchForm({ ...branchForm, estatus: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      >
                        {BRANCH_STATUSES.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1">Ciudad / Estado</label>
                      <input
                        type="text"
                        value={branchForm.ciudadEstado}
                        onChange={e => setBranchForm({ ...branchForm, ciudadEstado: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-400 mb-1">Centro Comercial / Mall</label>
                      <input
                        type="text"
                        value={branchForm.centroComercial}
                        onChange={e => setBranchForm({ ...branchForm, centroComercial: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1">Contacto en Tienda / Rampa</label>
                      <input
                        type="text"
                        value={branchForm.contactoTienda}
                        onChange={e => setBranchForm({ ...branchForm, contactoTienda: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      />
                    </div>
                  </div>

                  {/* Sustratos de Ingeniería */}
                  <div className="p-3 rounded-xl bg-page border border-amber-500/30 space-y-2">
                    <p className="text-[11px] font-bold text-amber-300 uppercase">
                      ⚙️ Parámetros de Instalación Civil en Tienda (Anclajes / Kits)
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div>
                        <label className="block text-slate-400 mb-1">Sustrato Pared</label>
                        <select
                          value={branchForm.sustratoPared}
                          onChange={e => setBranchForm({ ...branchForm, sustratoPared: e.target.value })}
                          className="w-full px-2.5 py-1.5 bg-surface border border-borderSubtle rounded-lg text-white"
                        >
                          {WALL_SUBSTRATES.map(w => (
                            <option key={w} value={w}>{w}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-slate-400 mb-1">Sustrato Piso</label>
                        <select
                          value={branchForm.sustratoPiso}
                          onChange={e => setBranchForm({ ...branchForm, sustratoPiso: e.target.value })}
                          className="w-full px-2.5 py-1.5 bg-surface border border-borderSubtle rounded-lg text-white"
                        >
                          {FLOOR_SUBSTRATES.map(f => (
                            <option key={f} value={f}>{f}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-slate-400 mb-1">% Drywall Mixto</label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={branchForm.distribucionDrywallPct}
                          onChange={e => setBranchForm({ ...branchForm, distribucionDrywallPct: Number(e.target.value) })}
                          className="w-full px-2.5 py-1.5 bg-surface border border-borderSubtle rounded-lg text-white"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {editorMode.entityType === 'project' && (
                <>
                  <div>
                    <label className="block text-slate-400 mb-1">Nombre del Proyecto / Obra (BD_Proyectos) *</label>
                    <input
                      type="text"
                      required
                      value={projectForm.nombre}
                      onChange={e => setProjectForm({ ...projectForm, nombre: e.target.value })}
                      placeholder="Ej: PRY-2026-0050: FARMATODO - Sambil Valencia"
                      className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white font-semibold"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-400 mb-1">Cliente Corporativo</label>
                      <select
                        value={projectForm.clienteId}
                        onChange={e => setProjectForm({ ...projectForm, clienteId: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      >
                        <option value="">-- Seleccionar Cliente --</option>
                        {clients.map(c => (
                          <option key={c.id} value={c.id}>{c.nombre}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1">Sucursal Física</label>
                      <select
                        value={projectForm.sucursalId}
                        onChange={e => setProjectForm({ ...projectForm, sucursalId: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      >
                        <option value="">-- Seleccionar Sucursal --</option>
                        {branches
                          .filter(b => !projectForm.clienteId || b.clienteId === projectForm.clienteId)
                          .map(b => (
                            <option key={b.id} value={b.id}>{b.nombre}</option>
                          ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-slate-400 mb-1">Estado del Proyecto</label>
                      <select
                        value={projectForm.estado}
                        onChange={e => setProjectForm({ ...projectForm, estado: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      >
                        {PROJECT_STATES.map(st => (
                          <option key={st} value={st}>{st}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1">Fecha de Inicio</label>
                      <input
                        type="date"
                        value={projectForm.fechaInicio}
                        onChange={e => setProjectForm({ ...projectForm, fechaInicio: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1">Fecha de Cierre</label>
                      <input
                        type="date"
                        value={projectForm.fechaCierre}
                        onChange={e => setProjectForm({ ...projectForm, fechaCierre: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-slate-400 mb-1">Notas Técnicas / Alcance</label>
                    <textarea
                      rows={3}
                      value={projectForm.notas}
                      onChange={e => setProjectForm({ ...projectForm, notas: e.target.value })}
                      className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                    />
                  </div>
                </>
              )}

              {editorMode.entityType === 'contact' && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-400 mb-1">Nombre Completo *</label>
                      <input
                        type="text"
                        required
                        value={contactForm.nombre}
                        onChange={e => setContactForm({ ...contactForm, nombre: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1">Cédula / Documento</label>
                      <input
                        type="text"
                        value={contactForm.cedula}
                        onChange={e => setContactForm({ ...contactForm, cedula: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white font-mono"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-400 mb-1">Teléfono</label>
                      <input
                        type="text"
                        value={contactForm.telefono}
                        onChange={e => setContactForm({ ...contactForm, telefono: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1">Correo Electrónico</label>
                      <input
                        type="email"
                        value={contactForm.email}
                        onChange={e => setContactForm({ ...contactForm, email: e.target.value })}
                        className="w-full px-3 py-2 bg-page border border-borderSubtle rounded-lg text-white"
                      />
                    </div>
                  </div>
                </>
              )}

              {!token && (
                <div className="pt-2 border-t border-borderSubtle">
                  <label className="block text-amber-300 font-semibold mb-1">
                    🔒 PIN de Supervisor (Requerido si no hay sesión JWT activa)
                  </label>
                  <input
                    type="password"
                    value={supervisorPIN}
                    onChange={e => setSupervisorPIN(e.target.value)}
                    placeholder="Ingrese PIN de 4 dígitos..."
                    className="w-full px-3 py-1.5 bg-page border border-amber-500/40 rounded-lg text-white font-mono"
                  />
                </div>
              )}
            </div>

            <div className="px-5 py-3 bg-surfaceHigh border-t border-borderSubtle flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditorMode(prev => ({ ...prev, open: false }))}
                className="px-4 py-1.5 rounded-lg text-xs font-semibold text-slate-300 hover:bg-surfaceHighest"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold bg-teal-500 hover:bg-teal-400 text-slate-950 transition disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                <span>Guardar en Notion ERP</span>
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
